import { ButtonView, ClickObserver, Plugin } from 'ckeditor5';
import { ConditionState } from './State';
import { InsertConditionCommand } from './insertCommand';
import { parseMarkerName } from './markerName';
/**
 * Condition authoring entry points.
 *
 * The plugin owns no editing form. It resolves *which* condition the author means and hands that to
 * `conditions.onEditRequest`, so the surrounding app can render the editor with its own component
 * library and its own knowledge of the question list.
 */
export class ConditionsUI extends Plugin {
    static get pluginName() {
        return 'ConditionsUI';
    }
    init() {
        const { editor } = this;
        if (editor.config.get('conditions.readonly')) {
            return;
        }
        this.insertCommand = editor.commands.get(InsertConditionCommand.pluginName);
        this.state = editor.plugins.get(ConditionState);
        this.registerClickHandler();
        this.registerInsertButton();
    }
    get onEditRequest() {
        return this.editor.config.get('conditions.onEditRequest');
    }
    /**
     * Opens the editor when the author clicks a condition's **marker pill** — the chip at either end of
     * the range — and only then.
     *
     * Clicking the wrapped text must not open it: that would make the passage unusable for ordinary
     * editing, since placing the caret to fix a typo would throw a panel over the text, and nested
     * conditions would resolve to the innermost one whatever the author was reaching for.
     */
    registerClickHandler() {
        const { editor } = this;
        editor.editing.view.addObserver(ClickObserver);
        this.listenTo(editor.editing.view.document, 'click', (evt, data) => {
            if (!this.onEditRequest) {
                return;
            }
            const domTarget = data.domEvent.target;
            const labelEl = domTarget.closest('.condition-label');
            if (!labelEl) {
                return;
            }
            const markerName = labelEl.getAttribute('data-marker-name');
            if (markerName && editor.model.markers.has(markerName)) {
                this.requestEdit(markerName, { anchor: labelEl });
                evt.stop();
            }
        }, { priority: 'high' });
    }
    registerInsertButton() {
        const { editor } = this;
        editor.ui.componentFactory.add('insertCondition', () => {
            const button = new ButtonView();
            button.bind('isEnabled').to(this.insertCommand);
            button.set({
                withText: true,
                label: 'Create Condition',
                class: 'pleditor-toolbar-fa-icon-button',
                tooltip: 'Add conditional marker to selected text',
            });
            button.on('execute', () => {
                // Insert unconfigured, then ask the app to configure it. Nothing invents a ref.
                const markerName = this.insertCommand.execute();
                if (markerName) {
                    this.requestEdit(markerName, { isNew: true });
                }
            });
            return button;
        });
    }
    /**
     * Hands a condition to the app's editor. `anchor` is supplied only when a pill was clicked;
     * otherwise the app locates it via `[data-marker-name]` once the pill has been rendered.
     */
    requestEdit(markerName, options = {}) {
        const { anchor = null, isNew = false } = options;
        const { onEditRequest } = this;
        if (!onEditRequest) {
            return;
        }
        const parsed = parseMarkerName(markerName);
        if (!parsed) {
            return;
        }
        this.state.activeCondition = markerName;
        const request = {
            markerName,
            predicate: parsed.predicate,
            ref: parsed.ref,
            choiceKey: parsed.choiceKey,
            anchor,
            isNew,
        };
        onEditRequest(request);
    }
}
